import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { courseAPI } from '../services/api';
import { toast } from 'react-toastify';
import { FiFolder, FiArrowLeft, FiSearch, FiFile, FiClipboard, FiLock, FiUnlock } from 'react-icons/fi';

function ImportContents() {
  const { courseId } = useParams();
  const navigate = useNavigate();
  
  const [course, setCourse] = useState(null);
  const [folders, setFolders] = useState([]);
  const [selectedFolder, setSelectedFolder] = useState('');
  const [searchKeyword, setSearchKeyword] = useState('');
  const [dynamicContent, setDynamicContent] = useState({
    courses: [],
    folder: [],
    videos: [],
    document: [],
    exam: []
  });
  const [selectedItems, setSelectedItems] = useState({
    folderid: [],
    videoids: [],
    docids: [],
    examid: []
  });
  const [currentFolderId, setCurrentFolderId] = useState(0);
  const [currentCourseId, setCurrentCourseId] = useState(courseId);
  const [showType, setShowType] = useState(0);
  const [loading, setLoading] = useState(false);
  const [breadcrumb, setBreadcrumb] = useState([]);

  // Fetch course and folders
  useEffect(() => {
    fetchImportData();
  }, [courseId]);

  // Fetch initial courses on load
  useEffect(() => {
    loadDynamicContent(0, courseId, 0, '');
  }, [courseId]);

  const fetchImportData = async () => {
    try {
      const response = await courseAPI.getImportContents(courseId);
      if (response.data.success) {
        setCourse(response.data.data.course);
        setFolders(response.data.data.folders);
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      console.error('Error fetching import data:', error);
      toast.error('Failed to load import data');
    }
  };

  const loadDynamicContent = useCallback(async (folderId, coursId, showtype, keyword = '') => {
    setLoading(true);
    try {
      const response = await courseAPI.getDynamicFolderImport({
        folderid: folderId,
        coursid: coursId,
        showtype: showtype,
        keyword: keyword
      });
      
      if (response.data.success) {
        setDynamicContent(response.data.data);
        setCurrentFolderId(folderId);
        setCurrentCourseId(coursId);
        setShowType(showtype);
      }
    } catch (error) {
      console.error('Error loading dynamic content:', error);
      toast.error('Failed to load content');
    } finally {
      setLoading(false);
    }
  }, []);

  const handleFolderClick = (folderId, coursId) => {
    loadDynamicContent(folderId, coursId, 1);
    setBreadcrumb([...breadcrumb, { folderId, coursId }]);
  };

  const handleCourseClick = (coursId) => {
    loadDynamicContent(0, coursId, 1);
    setBreadcrumb([{ folderId: 0, coursId }]);
    setCurrentCourseId(coursId);
  };

  const handleGoBack = () => {
    if (breadcrumb.length > 0) {
      const newBreadcrumb = [...breadcrumb];
      newBreadcrumb.pop();
      const previous = newBreadcrumb[newBreadcrumb.length - 1] || { folderId: 0, coursId: courseId };
      loadDynamicContent(previous.folderId, previous.coursId, breadcrumb.length > 1 ? 1 : 0);
      setBreadcrumb(newBreadcrumb);
    } else {
      loadDynamicContent(0, courseId, 0, searchKeyword);
    }
  };

  const handleSearchChange = useCallback((value) => {
    setSearchKeyword(value);
    const debounceTimer = setTimeout(() => {
      loadDynamicContent(0, courseId, 0, value);
    }, 300);
    return () => clearTimeout(debounceTimer);
  }, [courseId, loadDynamicContent]);

  const handleCheckboxChange = (type, id) => {
    setSelectedItems(prev => {
      const items = prev[type].includes(id)
        ? prev[type].filter(item => item !== id)
        : [...prev[type], id];
      return { ...prev, [type]: items };
    });
  };

  const handleImport = async (e) => {
    e.preventDefault();
    
    if (!selectedFolder) {
      toast.error('Please select a destination folder');
      return;
    }

    const hasSelection = Object.values(selectedItems).some(arr => arr.length > 0);
    if (!hasSelection) {
      toast.error('Please select at least one item to import');
      return;
    }

    try {
      const response = await courseAPI.importContents({
        coursid: courseId,
        parent_folder_id: selectedFolder,
        ...selectedItems
      });

      if (response.data.success) {
        toast.success(response.data.message);
        // Reset selection
        setSelectedItems({
          folderid: [],
          videoids: [],
          docids: [],
          examid: []
        });
        loadDynamicContent(currentFolderId, currentCourseId, showType, searchKeyword);
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      console.error('Error importing content:', error);
      toast.error('Failed to import content');
    }
  };

  // Build nested folder options
  const buildFolderOptions = (folders, level = 0, parentId = 0) => {
    const options = [];
    const filteredFolders = folders.filter(f => f.parent_folder_id === parentId);
    
    filteredFolders.forEach(folder => {
      options.push({
        value: folder.folder_id,
        label: '\u00A0'.repeat(level * 4) + folder.folders
      });
      
      // Recursively add children
      const children = buildFolderOptions(folders, level + 1, folder.folder_id);
      options.push(...children);
    });
    
    return options;
  };

  const folderOptions = buildFolderOptions(folders);

  return (
    <div className="container mx-auto px-4 py-6">
      <div className="mb-6">
        <button
          onClick={() => navigate(`/courses/${courseId}/addcontent`)}
          className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors flex items-center gap-2 mb-4"
        >
          <FiArrowLeft /> Go Back
        </button>
        <h1 className="text-2xl font-bold text-gray-900">
          CONTENTS FOR {course?.title?.toUpperCase()}
        </h1>
      </div>

      <div className="bg-white rounded-lg shadow p-6">
        <form onSubmit={handleImport}>
          <input type="hidden" value={courseId} />

          {/* Search and Folder Selection */}
          <div className="space-y-4 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Type folder name
                </label>
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Enter course name"
                    value={searchKeyword}
                    onChange={(e) => handleSearchChange(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <FiSearch className="absolute right-3 top-3 text-gray-400" />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Folder To Import
                </label>
                <select
                  value={selectedFolder}
                  onChange={(e) => setSelectedFolder(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                >
                  <option value="">Select Folder</option>
                  <option value="0">📁 Root Level (No Folder)</option>
                  {folderOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <hr className="my-6" />

          {/* Go Back Button */}
          {(showType > 0 || breadcrumb.length > 0) && (
            <div className="mb-4">
              <button
                type="button"
                onClick={handleGoBack}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors flex items-center gap-2"
              >
                <FiArrowLeft /> Go Back
              </button>
            </div>
          )}

          {/* Dynamic Content Area */}
          <div className="space-y-4">
            {loading ? (
              <div className="text-center py-8">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <p className="mt-2 text-gray-600">Loading...</p>
              </div>
            ) : (
              <>
                {/* Courses List */}
                {showType === 0 && dynamicContent.courses && dynamicContent.courses.length > 0 && (
                  <div className="space-y-2">
                    {dynamicContent.courses.map(course => (
                      <div
                        key={course.id}
                        className="border rounded-lg p-4 hover:bg-gray-50 cursor-pointer transition-colors"
                        onClick={() => handleCourseClick(course.id)}
                      >
                        <div className="flex items-center gap-4">
                          {course.course_thumbnail && (
                            <img
                              src={`${process.env.REACT_APP_BASE_URL}/${course.course_thumbnail}`}
                              alt={course.title}
                              className="w-24 h-16 object-cover rounded"
                            />
                          )}
                          <div>
                            <h3 className="font-semibold text-gray-900">{course.title}</h3>
                            {course.description && (
                              <p className="text-sm text-gray-600 mt-1">{course.description}</p>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Folders */}
                {dynamicContent.folder && dynamicContent.folder.length > 0 && (
                  <div className="space-y-2">
                    {dynamicContent.folder.map(folder => {
                      const videoCount = folder.video_count || 0;
                      const docCount = folder.doc_count || 0;
                      const examCount = folder.exam_count || 0;
                      
                      return (
                        <div key={folder.id} className="border rounded-lg p-4">
                          <div className="grid grid-cols-12 gap-4 items-center">
                            {/* Checkbox Column - Separate from clickable area */}
                            <div className="col-span-1 flex items-center justify-center">
                              <input
                                type="checkbox"
                                checked={selectedItems.folderid.includes(folder.folder_id)}
                                onChange={() => handleCheckboxChange('folderid', folder.folder_id)}
                                className="w-5 h-5 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                              />
                            </div>
                            
                            {/* Clickable Folder Area - Separate column */}
                            <div className="col-span-11">
                              <div
                                className="flex items-center gap-3 cursor-pointer hover:bg-gray-50 p-2 rounded transition-colors"
                                onClick={() => handleFolderClick(folder.folder_id, folder.course_id)}
                              >
                                <FiFolder className="text-yellow-500 text-4xl flex-shrink-0" />
                                <div>
                                  <p className="font-semibold text-gray-900">{folder.folders}</p>
                                  <div className="flex gap-4 text-sm text-gray-600 mt-1">
                                    {videoCount > 0 && <span>{videoCount} Videos</span>}
                                    {docCount > 0 && <span>{docCount} Files</span>}
                                    {examCount > 0 && <span>{examCount} Test Series</span>}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Exams */}
                {dynamicContent.exam && dynamicContent.exam.length > 0 && (
                  <div className="space-y-2">
                    {dynamicContent.exam.map(exam => (
                      <div key={exam.course_exam_id} className="border rounded-lg p-4">
                        <div className="grid grid-cols-12 gap-4 items-center">
                          <div className="col-span-1 flex items-center justify-center">
                            <input
                              type="checkbox"
                              checked={selectedItems.examid.includes(exam.id)}
                              onChange={() => handleCheckboxChange('examid', exam.id)}
                              className="w-5 h-5 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                            />
                          </div>
                          <div className="col-span-11 flex items-center gap-4">
                            <FiClipboard className="text-blue-500 text-2xl flex-shrink-0" />
                            <div className="flex-1">
                              <p className="font-semibold text-gray-900">{exam.exam}</p>
                              <p className="text-sm text-gray-600">Duration: {exam.duration}</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Videos */}
                {dynamicContent.videos && dynamicContent.videos.length > 0 && (
                  <div className="space-y-2">
                    {dynamicContent.videos.map(video => (
                      <div key={video.id} className="border rounded-lg p-4">
                        <div className="grid grid-cols-12 gap-4 items-center">
                          <div className="col-span-1 flex items-center justify-center">
                            <input
                              type="checkbox"
                              checked={selectedItems.videoids.includes(video.id)}
                              onChange={() => handleCheckboxChange('videoids', video.id)}
                              className="w-5 h-5 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                            />
                          </div>
                          <div className="col-span-2">
                            <img
                              src={`http://i3.ytimg.com/vi/${video.video_id}/0.jpg`}
                              alt={video.title}
                              className="w-full h-16 object-cover rounded"
                            />
                          </div>
                          <div className="col-span-7">
                            <p className="font-semibold text-gray-900">{video.title}</p>
                            {video.description && (
                              <p className="text-sm text-gray-600 mt-1">{video.description}</p>
                            )}
                          </div>
                          <div className="col-span-2 text-right text-gray-600">
                            {video.status === '1' ? (
                              <><FiUnlock className="inline mr-1" /> Unlocked</>
                            ) : (
                              <><FiLock className="inline mr-1" /> Locked</>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Documents */}
                {dynamicContent.document && dynamicContent.document.length > 0 && (
                  <div className="space-y-2">
                    {dynamicContent.document.map(doc => (
                      <div key={doc.id} className="border rounded-lg p-4">
                        <div className="grid grid-cols-12 gap-4 items-center">
                          <div className="col-span-1 flex items-center justify-center">
                            <input
                              type="checkbox"
                              checked={selectedItems.docids.includes(doc.id)}
                              onChange={() => handleCheckboxChange('docids', doc.id)}
                              className="w-5 h-5 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                            />
                          </div>
                          <div className="col-span-1 flex items-center justify-center">
                            <FiFile className="text-gray-500 text-4xl" />
                          </div>
                          <div className="col-span-7">
                            <p className="font-semibold text-gray-900">{doc.doc_name}</p>
                            {doc.description && (
                              <p className="text-sm text-gray-600 mt-1">{doc.description}</p>
                            )}
                          </div>
                          <div className="col-span-3 text-right text-gray-600">
                            {doc.status === '1' ? (
                              <><FiUnlock className="inline mr-1" /> Unlocked</>
                            ) : (
                              <><FiLock className="inline mr-1" /> Locked</>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Empty State */}
                {showType === 0 && dynamicContent.courses.length === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    No courses found. Try adjusting your search.
                  </div>
                )}

                {showType > 0 &&
                  dynamicContent.folder.length === 0 &&
                  dynamicContent.videos.length === 0 &&
                  dynamicContent.document.length === 0 &&
                  dynamicContent.exam.length === 0 && (
                    <div className="text-center py-8 text-gray-500">
                      <h3>You haven't uploaded any content yet.</h3>
                      <p>Select the content from the right panel to start adding here</p>
                    </div>
                  )}
              </>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end gap-4 mt-6">
            <button
              type="reset"
              onClick={() => setSelectedItems({
                folderid: [],
                videoids: [],
                docids: [],
                examid: []
              })}
              className="px-6 py-2 border border-gray-300 text-gray-700 rounded hover:bg-gray-50 transition-colors"
            >
              Reset
            </button>
            <button
              type="submit"
              className="px-6 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
            >
              Save Now
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default ImportContents;

